import React from "react";
import { LeafletMouseEvent, PathOptions } from "leaflet";
import { useTranslation } from "react-i18next";
import { FeatureGroup, Polyline, Popup } from "react-leaflet";
import styled from "styled-components";

import variableColors from "../../_App.module.scss";
import { configAgencyName, statuteMile } from "../../config";
import { AgencyContext } from "../../hooks/AgencyContext";

const { darkColor } = variableColors;

export const InfoPopup = styled.div<{ $width: string }>`
  width: ${(props) => props.$width};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-content: center;
  gap: 0.25rem;
  p {
    width: 100%;
    margin-top: 0;
    margin-bottom: 0;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    align-content: center;
    gap: 0.25rem;
    span {
      min-width: 4rem;
      text-align: center;
      font-weight: bold;
      float: right;
    }
  }
  table.bus_table2 {
    margin-top: 1rem;
  }
`;

//* PolylinePopup
const PolylinePopup = ({
  width,
  routeObj,
  directionSet,
  polylineOptions,
}: {
  width: string;
  routeObj: RouteObj;
  directionSet: Direction;
  polylineOptions: PathOptions;
}): JSX.Element => {
  const { t } = useTranslation();

  //* AgencyContext
  const { speedUnit } = React.useContext(AgencyContext) as AgencyContextType;

  return (
    <Popup>
      <InfoPopup $width={width}>
        <p>
          {t("Id")}{" "}
          <span style={{ background: routeObj?.background, color: routeObj?.color, padding: "0.25rem 0.5rem" }}>
            {routeObj?.shortName || routeObj?.id || t("b/d")}
          </span>
        </p>
        <p>
          {t("Nazwa")} <span>{routeObj?.name || t("b/d")}</span>
        </p>
        <p>
          {t("Dokąd")}: <span> {directionSet?.headsign || t("b/d")}</span>
        </p>
        <p>
          {t("Długość")}:{" "}
          <span>
            {speedUnit === "kmh"
              ? `${(directionSet?.length / 1000)?.toFixed(2)} km`
              : speedUnit === "mph"
                ? `${(directionSet?.length / (1000 * statuteMile))?.toFixed(2)} miles`
                : t("b/d")}
          </span>
        </p>
        <p>
          {t("Kierunek")}:{" "}
          <span
            style={{
              color: polylineOptions?.color,
              padding: "0.2rem",
              border: `1px solid ${polylineOptions?.color}`,
            }}
          >
            {directionSet?.directionId || t("b/d")}
          </span>
        </p>
        <p className="small text-muted">
          {t("Shape")}: <span>{directionSet?.tripPattern || t("b/d")}</span>
        </p>
      </InfoPopup>
    </Popup>
  );
};

// All Polylines To Render
const PolylineToRender = (
  routeObj: RouteObj,
  directionSet: Direction,
  index: number,
  polylineOptions: PathOptions,
  showPopup: boolean
): JSX.Element => {
  return (
    <Polyline
      key={index + routeObj?.id}
      pathOptions={polylineOptions}
      positions={directionSet?.loc}
      children={
        showPopup || configAgencyName === ("SweetWater" as string) ? (
          <PolylinePopup routeObj={routeObj} directionSet={directionSet} polylineOptions={polylineOptions} width="auto" />
        ) : null
      }
    />
  );
};

export const AllSelectedRoutesPolylines = ({
  selectedRoutes,
  routeToShow = "",
  blackRoutes = false,
  showPopup = false,
}: {
  selectedRoutes: RouteObj[];
  routeToShow?: string;
  blackRoutes?: boolean;
  showPopup?: boolean;
}): JSX.Element => {
  const [polylineRef, setPolylineRef] = React.useState<L.FeatureGroup<L.Polyline> | null>(null); //* Current Polyline Ref (RoutesInfo)

  //* Current Polyline Ref (RoutesInfo)
  React.useEffect(() => {
    if (polylineRef) {
      polylineRef.bringToFront();
    }
  }, [polylineRef]);

  // General Polyline Options
  const generalPathOptions = { smoothFactor: 3, opacity: 1 };

  const allSelectedRoutesPolylines: JSX.Element[] = selectedRoutes?.map((route: RouteObj, index: number): JSX.Element => {
    return (
      <FeatureGroup
        ref={(refElem: L.FeatureGroup<L.Polyline>) => {
          if (route?.id === routeToShow) {
            setPolylineRef(refElem);
          }
        }}
        key={index}
        eventHandlers={{
          mouseover: (event: LeafletMouseEvent) => {
            event.target.setStyle({ weight: 6 });
            event.target.bringToFront();
          },
          mouseout: (event: LeafletMouseEvent) => event.target.setStyle({ weight: 2 }),
        }}
      >
        <React.Fragment key={index}>
          {route.shapesDirection_0 && route.shapesDirection_0?.length
            ? route.shapesDirection_0?.map((elem: Direction, internalIndex: number) => {
                return PolylineToRender(
                  route,
                  elem,
                  internalIndex,
                  {
                    ...generalPathOptions,
                    color: blackRoutes ? darkColor : route?.background,
                    weight: route?.id === routeToShow ? 8 : 4,
                  },
                  showPopup
                );
              })
            : null}
          {route.shapesDirection_1 && route.shapesDirection_1?.length
            ? route.shapesDirection_1?.map((elem: Direction, internalIndex: number) => {
                return PolylineToRender(
                  route,
                  elem,
                  internalIndex,
                  {
                    ...generalPathOptions,
                    color: blackRoutes ? darkColor : route?.background,
                    weight: route?.id === routeToShow ? 8 : 4,
                  },
                  showPopup
                );
              })
            : null}
        </React.Fragment>
      </FeatureGroup>
    );
  });

  return <React.Fragment>{allSelectedRoutesPolylines}</React.Fragment>;
};
